import { turbosSdk } from './turbos-sdk';
import { type BalanceManager, DeepBookClient, DeepBookConfig } from '@mysten/deepbook-v3';
import { config } from '@config';
import { coinWithBalance, Transaction } from '@mysten/sui/transactions';
import { Decimal } from 'turbos-clmm-sdk';
import { type SuiTransactionBlockResponse } from '@mysten/sui/client';
import { SUI_CLOCK_OBJECT_ID } from '@mysten/sui/utils';

const PACKAGE_ID = '0x0544a64c44fa947b01c78389184ec316c5174ce916634e03a35a44601ca4576f';
const MANAGER = '0x389eb415cf290264df576e9b51d64cc85148be2a29482f6455d62ea438a8ec16';
const GAS_BUDGET = 0.5 * 500000000;

class DeepbookMarketMaker {
  address!: string;
  client!: DeepBookClient;
  protected managerKey!: string;
  deepBookConfig!: DeepBookConfig;

  constructor(address: string) {
    this.updateAddress(address);
  }

  protected get deepbook() {
    return this.client.deepBook;
  }

  protected get sui() {
    return this.client.client;
  }

  getBalanceManagerAddress() {
    return this.deepBookConfig.getBalanceManager(this.managerKey).address;
  }

  updateAddress(address: string, managerAddress?: string, managerTradeCap?: string) {
    this.managerKey = 'TURBOS_MANAGER_1';
    const balanceManagers: { [x: string]: BalanceManager } = {
      [this.managerKey]: {
        address: managerAddress || '',
        tradeCap: managerTradeCap || '',
      },
    };

    this.address = address;

    this.client = new DeepBookClient({
      address,
      env: config.deepbook_env,
      client: turbosSdk.provider,
      balanceManagers,
    });

    this.deepBookConfig = new DeepBookConfig({
      address,
      env: config.deepbook_env,
      balanceManagers,
    });
  }

  swapExactBaseForQuote(data: {
    poolKey: string;
    amountIn: number | string;
    amountOut: number | string;
    deepbookFee: number | string;
    slippage: number | string;
  }) {
    const { poolKey, amountIn, amountOut, deepbookFee: _, slippage } = data;
    const minQuote = new Decimal(amountOut)
      .minus(new Decimal(amountOut).mul(slippage).div(100))
      .toFixed(0);
    const tx = new Transaction();
    const pool = this.findPoolByKey(poolKey)!;
    const baseCoin = this.findCoinBySymbol(pool.baseCoin)!;
    const quoteCoin = this.findCoinBySymbol(pool.quoteCoin)!;

    tx.setGasBudgetIfNotSet(GAS_BUDGET);
    tx.setSenderIfNotSet(this.address);

    const [baseOut, quoteOut] = tx.moveCall({
      target: `${PACKAGE_ID}::sponsored_deep::swap_exact_base_for_quote_sponsored`,
      typeArguments: [baseCoin.type, quoteCoin.type],
      arguments: [
        tx.object(MANAGER),
        tx.object(pool.address),
        coinWithBalance({ type: baseCoin.type, balance: Number(amountIn) }),
        tx.pure.u64(minQuote),
        tx.object(SUI_CLOCK_OBJECT_ID),
      ],
    });
    tx.transferObjects([baseOut!, quoteOut!], tx.pure.address(this.address));

    // const [baseOut, quoteOut, deepOut] = this.deepbook.swapExactBaseForQuote({
    //   poolKey: poolKey,
    //   amount: this.scaleIn(amountIn, pool.baseCoin),
    //   deepAmount: Number(deepbookFee),
    //   minOut: this.scaleIn(minQuote, pool.quoteCoin),
    // })(tx);
    // tx.transferObjects([baseOut, quoteOut, deepOut], tx.pure.address(this.address));
    return tx;
  }

  swapExactQuoteForBase(data: {
    poolKey: string;
    amountIn: number | string;
    amountOut: number | string;
    deepbookFee: number | string;
    slippage: number | string;
  }) {
    const { poolKey, amountIn, amountOut, deepbookFee: _, slippage } = data;
    const minBase = new Decimal(amountOut)
      .minus(new Decimal(amountOut).mul(slippage).div(100))
      .toFixed(0);
    const tx = new Transaction();
    const pool = this.findPoolByKey(poolKey)!;
    const baseCoin = this.findCoinBySymbol(pool.baseCoin)!;
    const quoteCoin = this.findCoinBySymbol(pool.quoteCoin)!;

    tx.setGasBudgetIfNotSet(GAS_BUDGET);
    tx.setSenderIfNotSet(this.address);

    const [baseOut, quoteOut] = tx.moveCall({
      target: `${PACKAGE_ID}::sponsored_deep::swap_exact_quote_for_base_sponsored`,
      typeArguments: [baseCoin.type, quoteCoin.type],
      arguments: [
        tx.object(MANAGER),
        tx.object(pool.address),
        coinWithBalance({ type: quoteCoin.type, balance: Number(amountIn) }),
        tx.pure.u64(minBase),
        tx.object(SUI_CLOCK_OBJECT_ID),
      ],
    });
    tx.transferObjects([baseOut!, quoteOut!], tx.pure.address(this.address));

    // const [baseOut, quoteOut, deepOut] = this.deepbook.swapExactQuoteForBase({
    //   poolKey: poolKey,
    //   amount: this.scaleIn(amountIn, pool.quoteCoin),
    //   deepAmount: Number(deepbookFee),
    //   minOut: this.scaleIn(minBase, pool.baseCoin),
    // })(tx);
    // tx.transferObjects([baseOut, quoteOut, deepOut], tx.pure.address(this.address));
    return tx;
  }

  async devInspectSwapExactBaseForQuote(poolKey: string, amount: number | string) {
    const pool = this.findPoolByKey(poolKey)!;
    const result = await this.client.getQuoteQuantityOut(
      poolKey,
      this.scaleIn(amount, pool.baseCoin),
    );

    return {
      amountOut: result.quoteOut,
      amountIn: result.baseQuantity,
      deepbookFee: result.deepRequired,
    };
  }

  async devInspectSwapExactQuoteForBase(poolKey: string, amount: number | string) {
    const pool = this.findPoolByKey(poolKey)!;
    const result = await this.client.getBaseQuantityOut(
      poolKey,
      this.scaleIn(amount, pool.quoteCoin),
    );

    return {
      amountIn: result.quoteQuantity,
      amountOut: result.baseOut,
      deepbookFee: result.deepRequired,
    };
  }

  async checkManagerBalance(coinKey: string) {
    return await this.client.checkManagerBalance(this.managerKey, coinKey);
  }

  async getOrder(poolKey: string, orderId: string) {
    return await this.client.getOrder(poolKey, orderId);
  }

  async getQuoteQuantityOut(poolKey: string, baseQuantity: number) {
    return await this.client.getQuoteQuantityOut(poolKey, baseQuantity);
  }

  async getBaseQuantityOut(poolKey: string, quoteQuantity: number) {
    return await this.client.getQuoteQuantityOut(poolKey, quoteQuantity);
  }

  async getQuantityOut(poolKey: string, baseQuantity: number, quoteQuantity: number) {
    return await this.client.getQuantityOut(poolKey, baseQuantity, quoteQuantity);
  }

  async accountOpenOrders(poolKey: string) {
    return await this.client.accountOpenOrders(poolKey, this.managerKey);
  }

  async midPrice(poolKey: string) {
    return await this.client.midPrice(poolKey);
  }

  async getLevel2Range(poolKey: string, lowPrice: number, highPrice: number, isBid: boolean) {
    return await this.client.getLevel2Range(poolKey, lowPrice, highPrice, isBid);
  }

  createAndShareBalanceManager() {
    const tx = new Transaction();
    this.client.balanceManager.createAndShareBalanceManager()(tx);
    return tx;
  }

  mintTradeCap(txb: Transaction) {
    const tx = txb || new Transaction();
    const balanceManagerAddress = this.deepBookConfig.getBalanceManager(this.managerKey).address;
    const tradeCap = tx.moveCall({
      target: `${this.deepBookConfig.DEEPBOOK_PACKAGE_ID}::balance_manager::mint_trade_cap`,
      arguments: [tx.object(balanceManagerAddress)],
    });

    tx.transferObjects([tradeCap], tx.pure.address(this.address));

    return tx;
  }

  placeLimitOrder(
    poolKey: string,
    price: number,
    quantity: number,
    isBid: boolean,
    txb?: Transaction,
  ) {
    const tx = txb || new Transaction();
    this.client.deepBook.placeLimitOrder({
      poolKey: poolKey,
      balanceManagerKey: this.managerKey,
      clientOrderId: Date.now().toString(),
      price,
      quantity,
      isBid,
    })(tx);

    return tx;
  }

  placeMarketOrder(poolKey: string, quantity: number, isBid: boolean, txb?: Transaction) {
    const tx = txb || new Transaction();
    this.client.deepBook.placeMarketOrder({
      poolKey: poolKey,
      balanceManagerKey: this.managerKey,
      clientOrderId: Date.now().toString(),
      quantity,
      isBid,
    })(tx);
    return tx;
  }

  cancelOrder(poolKey: string, orderId: string) {
    const tx = new Transaction();
    this.client.deepBook.cancelOrder(poolKey, this.managerKey, orderId)(tx);
    return tx;
  }

  depositIntoManager(coinKey: string, amountToDeposit: number, txb?: Transaction) {
    const tx = txb || new Transaction();
    this.client.balanceManager.depositIntoManager(this.managerKey, coinKey, amountToDeposit)(tx);
    return tx;
  }

  withdrawFromManager(coinKey: string, amountToWithdraw: number, recipient: string) {
    const tx = new Transaction();
    this.client.balanceManager.withdrawFromManager(
      this.managerKey,
      coinKey,
      amountToWithdraw,
      recipient,
    )(tx);
    return tx;
  }

  withdrawAllFromManager(coinKey: string, recipient: string) {
    const tx = new Transaction();
    this.client.balanceManager.withdrawAllFromManager(this.managerKey, coinKey, recipient)(tx);
    return tx;
  }

  findPool(coinTypeA: string, coinTypeB: string) {
    const coinA = this.findCoinByType(coinTypeA);
    const coinB = this.findCoinByType(coinTypeB);
    if (!coinA || !coinB) return;
    return config.deepbook_pools.find(
      (pool) => pool.baseCoin === coinA.symbol && pool.quoteCoin === coinB.symbol,
    );
  }

  findPoolByAddress(address: string) {
    return config.deepbook_pools.find((pool) => pool.address === address);
  }

  findPoolByKey(poolKey: string) {
    return config.deepbook_pools.find((pool) => pool.poolKey === poolKey);
  }

  findCoinByType(type: string) {
    return config.deepbook_coins.find((coin) => coin.type === type);
  }

  findCoinBySymbol(symbol: string) {
    return config.deepbook_coins.find((coin) => coin.symbol === symbol);
  }

  findShareObject(res: SuiTransactionBlockResponse) {
    // @ts-ignore
    const balanceManagerAddress = res.objectChanges?.find((change) => {
      return change.type === 'created' && change.objectType.includes('BalanceManager');
    })?.['objectId'] as string;

    return balanceManagerAddress;
  }

  protected scaleIn(amount: number | string, symbol: string) {
    const coin = config.deepbook_coins.find((coin) => coin.symbol === symbol)!;
    return new Decimal(amount).div(10 ** coin.decimals).toNumber();
  }
}

export const deepbook = new DeepbookMarketMaker('0x0');
